// Global Instructions Rule Applied!
//
// useConsolidatedCommitmentForm — the state of the Consolidated commitment
// form: one fund_group_commitments row, its split across the member funds and
// the notice documents its commitments carry.
//
// The form holds a draft and saves explicitly through fg__save_subscription.
// An edit sends only what changed; the allocation set goes along whenever the
// amount or a satellite row changed (the primary fund takes what the
// satellites leave). A member fund's commitment that would fall below what
// has been called comes back as a 409 warning: the form lists the funds,
// asks, and saves again with force.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Modal } from 'antd'
import { toast } from 'react-toastify'

import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { summariseFieldErrors } from '../../../core/utils/utilities.ts'
import { useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import type { FieldValue } from '../../../core/components/InputManager/types.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { TABLE_CONSOLIDATED_COMMITMENTS } from '../../../state_manager/globals.ts'
import { updateTabTitle } from '../../../store/slices/tabSlice'
import type { FundGroupCommitmentDetail } from '../../../state_manager/data_models/commitment_form.ts'
import {
  deleteFundGroupSubscription,
  saveFundGroupSubscription
} from '../../fund_groups/services/fundGroupsService.ts'
import { fetchFundGroupCommitmentDetail } from '../services/commitmentsService.ts'
import {
  CommitmentFieldKeys,
  EMPTY_CORE_DRAFT,
  EMPTY_NOTICE_DRAFT,
  allocationsFromConsolidated,
  applyCoreChange,
  buildConsolidatedEditPayload,
  commitmentAmountAllowed,
  consolidatedDraftChanged,
  coreFromConsolidated,
  noticeFromConsolidated,
  summariseAllocations,
  type AllocationRowDraft,
  type CommitmentNoticeDraft,
  type ConsolidatedDraft
} from '../utils/commitmentDrafts.ts'
import {
  CARRY_CONSOLIDATED_HELP,
  GP_CONSOLIDATED_HELP,
  getCarryRecipientField,
  getCommitmentFields,
  getGpField
} from '../utils/commitmentFieldConfigs.ts'
import { commitmentTabTitle } from './useCommitmentForm.tsx'
import { useCommitmentFormOptions } from './useCommitmentFormOptions.ts'

const VALIDATION_TOAST_KEY = 'consolidated-commitment-form-required'
const EMPTY_DRAFT: ConsolidatedDraft = { core: EMPTY_CORE_DRAFT, notice: EMPTY_NOTICE_DRAFT, allocations: [] }

const draftFromDetail = (detail: FundGroupCommitmentDetail): ConsolidatedDraft => ({
  core: coreFromConsolidated(detail),
  notice: noticeFromConsolidated(detail),
  allocations: allocationsFromConsolidated(detail)
})

type AllocationPatch = Partial<Pick<AllocationRowDraft, 'amount' | 'status_id' | 'date_closed'>>

/**
 * One satellite row after a change. A satellite fund that gets its first
 * amount starts pending (Committed, no date — awaiting approval) unless the
 * user says otherwise; an existing allocation keeps what it has.
 */
export const patchAllocationRow = (
  rows: AllocationRowDraft[],
  memberId: string,
  patch: AllocationPatch,
  committedStatusId: number | null
): AllocationRowDraft[] =>
  rows.map((row) => {
    if (row.memberId !== memberId || row.isPrimary) return row
    const next = { ...row, ...patch }
    const startsNow = !((row.amount ?? 0) > 0) && (next.amount ?? 0) > 0
    if (startsNow && row.pocketId == null && next.status_id == null && !('status_id' in patch)) {
      next.status_id = committedStatusId
    }
    return next
  })

/** The satellite rows with a default share and no amount yet take that share. */
export const applyDefaultAllocations = (
  rows: AllocationRowDraft[],
  amount: number,
  committedStatusId: number | null
): AllocationRowDraft[] =>
  rows.map((row) => {
    if (row.isPrimary || row.defaultPct == null || (row.amount ?? 0) > 0) return row
    return {
      ...row,
      amount: Math.round(amount * row.defaultPct * 100) / 100,
      status_id: row.pocketId == null && row.status_id == null ? committedStatusId : row.status_id
    }
  })

export function useConsolidatedCommitmentForm(recordId: string) {
  const dispatch = useDispatch()
  const { closeFormTab } = useTabManagerContext()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()
  const currencyText = useNumberDisplay('currency')
  const formTabKey = `${TABLE_CONSOLIDATED_COMMITMENTS}-form-edit-${recordId}`

  const { statusOptions, noticeSupplementOptions } = useCommitmentFormOptions(null)
  // the pre-closing status (cfg.fund_commitment_statuses 'Committed') = pending
  const committedStatusId = useMemo(() => {
    const committed = statusOptions.find((s) => s.label === 'Committed')
    return typeof committed?.value === 'number' ? committed.value : null
  }, [statusOptions])

  const [detail, setDetail] = useState<FundGroupCommitmentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [baseline, setBaseline] = useState<ConsolidatedDraft>(EMPTY_DRAFT)
  const [draft, setDraft] = useState<ConsolidatedDraft>(EMPTY_DRAFT)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [validationErrorsVersion, setValidationErrorsVersion] = useState(0)
  // InputManager keeps what was typed and ignores a prop value for an edited
  // field, so putting the fields back (Cancel, a reload) remounts it
  const [resetVersion, setResetVersion] = useState(0)

  const applyDetail = useCallback(
    (next: FundGroupCommitmentDetail) => {
      const fresh = draftFromDetail(next)
      setDetail(next)
      setBaseline(fresh)
      setDraft(fresh)
      setResetVersion((v) => v + 1)
      dispatch(
        updateTabTitle({
          key: `${TABLE_CONSOLIDATED_COMMITMENTS}-form-edit-${next.fund_group_commitments__id}`,
          title: commitmentTabTitle(next.investors__name, next.fund_groups__name)
        })
      )
    },
    [dispatch]
  )

  const load = useCallback(async (): Promise<void> => {
    const res = await fetchFundGroupCommitmentDetail(recordId, getAccessToken(store.getState().auth))
    if (!res.data) {
      setNotFound(res.notFound === true)
      setLoadError(res.error ?? null)
      setDetail(null)
      return
    }
    setNotFound(false)
    setLoadError(null)
    applyDetail(res.data)
  }, [recordId, applyDetail])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      await load()
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [load])

  const payload = useMemo(
    () => (detail ? buildConsolidatedEditPayload(detail.fund_group_commitments__id, draft, baseline) : null),
    [detail, draft, baseline]
  )
  const dirty = detail != null && consolidatedDraftChanged(draft, baseline)

  /** Coming back to the tab: pick up what changed elsewhere (a GP switch on a
   *  member fund, the investor's documents), unless there are unsaved changes. */
  const refreshIfClean = useCallback(async () => {
    if (!dirty && !saving) await load()
  }, [dirty, saving, load])

  const commitmentFields = useMemo(
    () => getCommitmentFields(draft.core, { statusOptions, currencySymbol: detail?.currencies__symbol }),
    [draft.core, statusOptions, detail]
  )
  // the GP marker, then the carry marker: both set every pocket
  const gpFields = useMemo(
    () => [...getGpField(draft.core, GP_CONSOLIDATED_HELP), ...getCarryRecipientField(draft.core, CARRY_CONSOLIDATED_HELP)],
    [draft.core]
  )

  const handleChange = useCallback((key: string, value: FieldValue) => {
    setDraft((prev) => ({ ...prev, core: applyCoreChange(prev.core, key, value) }))
    setValidationErrors((prev) => {
      // an amount of 0 is refused or not by the carry marker
      const clear = key === CommitmentFieldKeys.is_carry_recipient ? [key, CommitmentFieldKeys.amount] : [key]
      if (!clear.some((k) => k in prev)) return prev
      const next = { ...prev }
      clear.forEach((k) => delete next[k])
      return next
    })
  }, [])

  const handleNoticeChange = useCallback((notice: CommitmentNoticeDraft) => {
    setDraft((prev) => ({ ...prev, notice }))
  }, [])

  const handleAllocationChange = useCallback(
    (memberId: string, patch: AllocationPatch) => {
      setDraft((prev) => ({
        ...prev,
        allocations: patchAllocationRow(prev.allocations, memberId, patch, committedStatusId)
      }))
    },
    [committedStatusId]
  )

  const handleApplyDefaults = useCallback(() => {
    setDraft((prev) =>
      prev.core.amount == null
        ? prev
        : { ...prev, allocations: applyDefaultAllocations(prev.allocations, prev.core.amount, committedStatusId) }
    )
  }, [committedStatusId])

  const handleCancel = useCallback(() => {
    dismissErrorToast(VALIDATION_TOAST_KEY)
    setDraft(baseline)
    setValidationErrors({})
    setResetVersion((v) => v + 1)
  }, [baseline])

  const handleClose = useCallback(() => {
    closeFormTab(formTabKey)
  }, [closeFormTab, formTabKey])

  const submit = useCallback(
    async (force: boolean): Promise<void> => {
      if (!payload) return
      setSaving(true)
      try {
        const res = await saveFundGroupSubscription(payload, force, getAccessToken(store.getState().auth))
        if (res.success) {
          toast.success('Consolidated commitment saved')
          await load()
          return
        }
        if (res.belowCalledWarning) {
          const lines = res.belowCalledWarning.pockets
            .map(
              (p) =>
                `${String(p.funds__name ?? '')}: new amount ${currencyText(Number(p.prog__new_amount ?? 0))} < called to date ${currencyText(Number(p.prog__called_to_date ?? 0))}`
            )
            .join('\n')
          modalApi.confirm({
            title: 'Fund commitment below called-to-date',
            content: (
              <div style={{ whiteSpace: 'pre-line' }}>
                {lines}
                {'\n'}Save anyway?
              </div>
            ),
            okText: 'Save anyway',
            onOk: () => submit(true)
          })
          return
        }
        showErrorToast(res.error || 'Failed to save the consolidated commitment')
      } finally {
        setSaving(false)
      }
    },
    [payload, load, modalApi, currencyText]
  )

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {}
    // 0 is an amount on the commitment that receives the carried interest
    if (!commitmentAmountAllowed(draft.core.amount, draft.core.is_carry_recipient)) {
      errors[CommitmentFieldKeys.amount] = 'Commitment is required'
    }
    if (draft.core.status_id == null) errors[CommitmentFieldKeys.status_id] = 'Status is required'
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      setValidationErrorsVersion((v) => v + 1)
      showErrorToast(summariseFieldErrors([errors]) ?? 'Please complete the required fields', {
        key: VALIDATION_TOAST_KEY
      })
      return
    }
    // (a commitment of 0 has no split to check: every member fund gets 0)
    if (draft.core.amount !== 0 && summariseAllocations(draft.allocations, draft.core, null, '').residual <= 0) {
      showErrorToast('The satellite allocations must leave a positive amount in the primary fund', {
        key: VALIDATION_TOAST_KEY
      })
      return
    }
    dismissErrorToast(VALIDATION_TOAST_KEY)
    await submit(false)
  }, [draft, submit])

  const handleDelete = useCallback(() => {
    if (!detail) return
    modalApi.confirm({
      title: 'Delete consolidated commitment?',
      content: `${detail.investors__name ?? ''} — ${detail.currencies__symbol ?? ''}${currencyText(detail.fund_group_commitments__amount)}. Its commitments in the member funds are removed with it.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleting(true)
        try {
          const res = await deleteFundGroupSubscription(
            detail.fund_group_commitments__id,
            getAccessToken(store.getState().auth)
          )
          if (res.success) {
            toast.success('Consolidated commitment deleted')
            closeFormTab(formTabKey)
          } else {
            showErrorToast(res.error || 'Failed to delete the consolidated commitment')
          }
        } finally {
          setDeleting(false)
        }
      }
    })
  }, [detail, modalApi, currencyText, closeFormTab, formTabKey])

  return {
    detail,
    loading,
    notFound,
    loadError,
    draft,
    commitmentFields,
    gpFields,
    statusOptions,
    noticeSupplementOptions,
    dirty,
    saving,
    deleting,
    validationErrors,
    validationErrorsVersion,
    resetVersion,
    modalContextHolder: modalContextHolder as React.ReactElement,
    handleChange,
    handleNoticeChange,
    handleAllocationChange,
    handleApplyDefaults,
    handleCancel,
    handleClose,
    handleSave,
    handleDelete,
    refreshIfClean
  }
}
